import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Panel } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { SettingsIntro } from "~/features/settings/section";
import { api, unwrap } from "~/lib/api";
import { useFormat, useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/settings/activity")({ component: ActivityPage });

/** Each audited entity opens its own page. */
const ENTITY_ROUTE: Partial<Record<string, EntityRoute>> = {
  customer: "/customers/$id",
  supplier: "/suppliers/$id",
  product: "/products/$id",
  order: "/orders/$id",
  quote: "/quotes/$id",
  invoice: "/invoices/$id",
  shipment: "/shipments/$id",
  purchase_order: "/purchasing/$id",
  sav: "/sav/$id",
  stock_count: "/stock/counts/$id",
};
type EntityRoute =
  | "/customers/$id"
  | "/suppliers/$id"
  | "/products/$id"
  | "/orders/$id"
  | "/quotes/$id"
  | "/invoices/$id"
  | "/shipments/$id"
  | "/purchasing/$id"
  | "/sav/$id"
  | "/stock/counts/$id";

function ActivityPage() {
  const t = useT();
  return (
    <>
      <SettingsIntro text={t("settings.pages.activity.subtitle")} />
      <ActivityLog />
    </>
  );
}

/** The latest changes across the company, by anyone. */
function ActivityLog() {
  const t = useT();
  const format = useFormat();
  const query = useQuery({
    queryKey: ["audit", "all"],
    queryFn: () => unwrap(api.audit.get({ query: { limit: 50, offset: 0 } })),
  });
  return (
    <Panel>
      <h2 className="px-5 pt-5 pb-3 text-[15px] font-medium md:px-6">{t("settings.audit")}</h2>
      {query.isPending ? (
        <div className="px-6 pb-6">
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <p className="px-6 pb-6 text-sm text-fg-3">{t("audit.emptyTitle")}</p>
      ) : (
        <ul className="divide-y divide-line border-t border-line">
          {query.data.map((entry) => {
            const to = entry.entityType ? ENTITY_ROUTE[entry.entityType] : undefined;
            const rowClass = "flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-5 py-3 md:px-6";
            const content = (
              <>
                <span className="text-sm">
                  {t(`audit.actions.${entry.action}`, { actor: entry.actorName ?? t("audit.system"), entity: entry.entityLabel })}
                </span>
                <time className="text-xs text-fg-3" dateTime={String(entry.at)} title={format.date(entry.at, "datetime")}>
                  {format.relative(entry.at)}
                </time>
              </>
            );
            return (
              <li key={entry.id}>
                {to && entry.entityId ? (
                  <Link to={to} params={{ id: entry.entityId }} className={`${rowClass} hover:bg-surface-2`}>
                    {content}
                  </Link>
                ) : (
                  <div className={rowClass}>{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
